import React from 'react';

import { isDefined } from '@nfctron/nexus-core';

import get from 'lodash/get';
import { Controller, type FieldErrors, type FieldValues, FormProvider, type Path, type SubmitHandler, useFormContext } from 'react-hook-form';

import { Checkbox } from '@root/features/form/components/Checkbox/Checkbox';
import { NumberInput } from '@root/features/form/components/NumberInput/NumberInput';
import { PasswordInput } from '@root/features/form/components/PasswordInput/PasswordInput';
import { RadioGroup } from '@root/features/form/components/RadioGroup/RadioGroup';
import { Select } from '@root/features/form/components/Select/Select';
import { TextInput } from '@root/features/form/components/TextInput/TextInput';

import { type ComponentDisplayName } from '@root/lib/types/components';

import {
	type FormCheckboxProps,
	type FormControlProps,
	type FormNumberInputProps,
	type FormPasswordInputProps,
	type FormProps,
	type FormRadioGroupProps,
	type FormSelectProps,
	type FormTextInputProps,
} from './Form.types';

/**
 * Form component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export function Form<TFieldValues extends FieldValues = FieldValues, TContext = any>({
	onSubmit,
	children,
	form,
	withInvisibleSubmit,
	...props
}: FormProps<TFieldValues, TContext>) {
	return (
		<FormProvider {...form}>
			<form onSubmit={form.handleSubmit(onSubmit as SubmitHandler<FieldValues>)} {...props}>
				{children}
				{/* Hidden submit button in order to correctly trigger onSubmit (eg. via enter key).
						Primarily used because of absence of submit button from within the provided children. */}
				{withInvisibleSubmit && <button type="submit" className="hidden" />}
			</form>
		</FormProvider>
	);
}

/**
 * Form component display name
 * @type {ComponentDisplayName}
 */
Form.displayName = 'component/Form';

/**
 * Form/Control component
 * @constructor
 */
function FormControl<TFieldValues extends FieldValues = FieldValues, TFieldName extends Path<TFieldValues> = Path<TFieldValues>>({
	fieldId,
	options: { disabled, ...rules } = {},
	transform,
	render,
}: FormControlProps<TFieldValues, TFieldName>) {
	const { control } = useFormContext<TFieldValues>();

	return (
		<Controller<TFieldValues, TFieldName>
			name={fieldId}
			rules={rules}
			control={control}
			render={({ field, formState, fieldState }) =>
				render({
					field: {
						...field,
						onChange: (value) => field.onChange(isDefined(transform) ? transform.output(value) : value),
						value: isDefined(transform) ? transform.input(field.value) : field.value,
					},
					formState,
					fieldState,
				})
			}
		/>
	);
}

/**
 * Form/Control component display name
 * @type {ComponentDisplayName}
 */
FormControl.displayName = 'component/FormControl';

/**
 * Form/TextInput component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
function FormTextInput<TFieldValues extends FieldValues = FieldValues, TFieldName extends Path<TFieldValues> = Path<TFieldValues>>({
	fieldId,
	options,
	transform,
	...rest
}: FormTextInputProps<TFieldValues, TFieldName>) {
	return (
		<FormControl<TFieldValues, TFieldName>
			fieldId={fieldId}
			options={options}
			transform={transform}
			render={({ field, formState }) => {
				const errors = get(formState.errors, fieldId) as FieldErrors<TFieldValues>[TFieldName];
				return (
					<TextInput
						{...field}
						error={isDefined(errors) && typeof errors.message === 'string' ? errors.message : undefined}
						{...rest}
						disabled={options?.disabled}
						withAsterisk={isDefined(options?.required)}
					/>
				);
			}}
		/>
	);
}

/**
 * Form/TextInput component display name
 * @type {ComponentDisplayName}
 */
FormTextInput.displayName = 'component/FormTextInput';

/**
 * Form/NumberInput component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
function FormNumberInput<TFieldValues extends FieldValues = FieldValues, TFieldName extends Path<TFieldValues> = Path<TFieldValues>>({
	fieldId,
	options,
	transform,
	...rest
}: FormNumberInputProps<TFieldValues, TFieldName>) {
	return (
		<FormControl<TFieldValues, TFieldName>
			fieldId={fieldId}
			options={options}
			transform={transform}
			render={({ field, formState }) => {
				const errors = get(formState.errors, fieldId) as FieldErrors<TFieldValues>[TFieldName];
				return (
					<NumberInput
						{...field}
						error={isDefined(errors) && typeof errors.message === 'string' ? errors.message : undefined}
						{...rest}
						disabled={options?.disabled}
						withAsterisk={isDefined(options?.required)}
					/>
				);
			}}
		/>
	);
}

/**
 * Form/NumberInput component display name
 * @type {ComponentDisplayName}
 */
FormNumberInput.displayName = 'component/FormNumberInput';

/**
 * Form/Select component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
function FormSelect<TFieldValues extends FieldValues = FieldValues, TFieldName extends Path<TFieldValues> = Path<TFieldValues>>({
	fieldId,
	options,
	transform,
	...rest
}: FormSelectProps<TFieldValues, TFieldName>) {
	return (
		<FormControl<TFieldValues, TFieldName>
			fieldId={fieldId}
			options={options}
			transform={transform}
			render={({ field, formState }) => {
				const errors = get(formState.errors, fieldId) as FieldErrors<TFieldValues>[TFieldName];
				return (
					<Select
						{...field}
						error={isDefined(errors) && typeof errors.message === 'string' ? errors.message : undefined}
						{...rest}
						data={rest.data ?? []}
						disabled={options?.disabled}
						withAsterisk={isDefined(options?.required)}
					/>
				);
			}}
		/>
	);
}

/**
 * Form/Select component display name
 * @type {ComponentDisplayName}
 */
FormSelect.displayName = 'component/FormSelect';

/**
 * Form/PasswordInput component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
function FormPasswordInput<TFieldValues extends FieldValues = FieldValues, TFieldName extends Path<TFieldValues> = Path<TFieldValues>>({
	fieldId,
	options,
	transform,
	...rest
}: FormPasswordInputProps<TFieldValues, TFieldName>) {
	return (
		<FormControl<TFieldValues, TFieldName>
			fieldId={fieldId}
			options={options}
			transform={transform}
			render={({ field, formState }) => {
				const errors = get(formState.errors, fieldId) as FieldErrors<TFieldValues>[TFieldName];
				return (
					<PasswordInput
						{...field}
						error={isDefined(errors) && typeof errors.message === 'string' ? errors.message : undefined}
						{...rest}
						disabled={options?.disabled}
						withAsterisk={isDefined(options?.required)}
					/>
				);
			}}
		/>
	);
}

/**
 * Form/PasswordInput component display name
 * @type {ComponentDisplayName}
 */
FormSelect.displayName = 'component/FormPasswordInput';

/**
 * Form/Checkbox component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
function FormCheckbox<TFieldValues extends FieldValues = FieldValues, TFieldName extends Path<TFieldValues> = Path<TFieldValues>>({
	fieldId,
	options,
	transform,
	...rest
}: FormCheckboxProps<TFieldValues, TFieldName>) {
	return (
		<FormControl<TFieldValues, TFieldName>
			fieldId={fieldId}
			options={options}
			transform={transform}
			render={({ field, formState }) => {
				const errors = get(formState.errors, fieldId) as FieldErrors<TFieldValues>[TFieldName];
				return (
					<Checkbox
						{...field}
						defaultChecked={field.value}
						error={isDefined(errors) && typeof errors.message === 'string' ? errors.message : undefined}
						{...rest}
						disabled={options?.disabled}
					/>
				);
			}}
		/>
	);
}

/**
 * Form/Checkbox component display name
 * @type {ComponentDisplayName}
 */
FormSelect.displayName = 'component/FormCheckbox';

/**
 * Form/RadioGroup component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
function FormRadioGroup<TFieldValues extends FieldValues = FieldValues, TFieldName extends Path<TFieldValues> = Path<TFieldValues>>({
	fieldId,
	data,
	options,
	transform,
	...rest
}: FormRadioGroupProps<TFieldValues, TFieldName>) {
	return (
		<FormControl<TFieldValues, TFieldName>
			fieldId={fieldId}
			options={options}
			transform={transform}
			render={({ field, formState }) => {
				const errors = get(formState.errors, fieldId) as FieldErrors<TFieldValues>[TFieldName];
				return (
					<RadioGroup
						{...field}
						data={data}
						defaultChecked={field.value}
						error={isDefined(errors) && typeof errors.message === 'string' ? errors.message : undefined}
						{...rest}
						disabled={options?.disabled}
					/>
				);
			}}
		/>
	);
}

/**
 * Form/RadioGroup component display name
 * @type {ComponentDisplayName}
 */
FormSelect.displayName = 'component/FormRadioGroup';

/** assign Form component shortcuts */
Form.Control = FormControl;
Form.TextInput = FormTextInput;
Form.NumberInput = FormNumberInput;
Form.Select = FormSelect;
Form.PasswordInput = FormPasswordInput;
Form.Checkbox = FormCheckbox;
Form.RadioGroup = FormRadioGroup;
